import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import {
    RefreshCw,
    AlertCircle,
    CheckCircle,
    Loader2,
    BookOpen,
    Trash2,
    Play,
    XCircle,
    RotateCcw
} from 'lucide-react';

const PlaybookManager = ({ onClose }) => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        setRefreshing(true);
        try {
            const response = await api.get('/job-simulations/admin/playbook-status');
            setStatus(response.data);
        } catch (error) {
            console.error('Failed to fetch playbook status:', error);
            toast.error('Failed to load playbook status');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleSelectAll = (skills) => {
        if (selectedSkills.length === skills.length) {
            setSelectedSkills([]);
        } else {
            setSelectedSkills(skills.map(s => s.id));
        }
    };

    const handleToggleSkill = (skillId) => {
        setSelectedSkills(prev => 
            prev.includes(skillId) 
                ? prev.filter(id => id !== skillId)
                : [...prev, skillId]
        );
    };

    const handleRetrySelected = async () => {
        if (selectedSkills.length === 0) {
            toast.error('Please select skills to retry');
            return;
        }

        setGenerating(true);
        try {
            const response = await api.post('/job-simulations/admin/retry-failed-playbooks', null, {
                params: { skill_ids: selectedSkills }
            });
            toast.success(`Queued ${response.data.skills_queued} skills for generation`);
            setSelectedSkills([]);
            
            // Refresh after a delay
            setTimeout(() => fetchStatus(), 5000);
        } catch (error) {
            console.error('Failed to retry playbooks:', error);
            toast.error('Failed to queue playbook generation');
        } finally {
            setGenerating(false);
        }
    };

    const handleRetryAll = async () => {
        setGenerating(true);
        try {
            const response = await api.post('/job-simulations/admin/retry-failed-playbooks');
            toast.success(`Queued ${response.data.skills_queued} skills for generation`);
            
            // Refresh after a delay
            setTimeout(() => fetchStatus(), 5000);
        } catch (error) {
            console.error('Failed to retry all playbooks:', error);
            toast.error('Failed to queue playbook generation');
        } finally {
            setGenerating(false);
        }
    };

    const handleDeletePlaybook = async (skillId) => {
        try {
            await api.delete(`/job-simulations/admin/playbook/${skillId}`);
            toast.success('Playbook deleted');
            fetchStatus();
        } catch (error) {
            console.error('Failed to delete playbook:', error);
            toast.error('Failed to delete playbook');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const allMissingSkills = [...(status?.missing_skills || []), ...(status?.failed_skills || [])];

    return (
        <div className="space-y-6" data-testid="playbook-manager">
            {/* Status Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-primary">{status?.total_skills || 0}</p>
                        <p className="text-xs text-muted-foreground">Total Skills</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-emerald-500">{status?.with_playbook || 0}</p>
                        <p className="text-xs text-muted-foreground">With Playbooks</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-amber-500">{status?.without_playbook || 0}</p>
                        <p className="text-xs text-muted-foreground">Missing</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-red-500">{status?.failed || 0}</p>
                        <p className="text-xs text-muted-foreground">Failed</p>
                    </CardContent>
                </Card>
            </div>

            {/* Coverage Progress */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Playbook Coverage</span>
                        <span className="text-sm font-bold text-primary">{status?.coverage_percentage || 0}%</span>
                    </div>
                    <Progress value={status?.coverage_percentage || 0} className="h-3" />
                </CardContent>
            </Card>

            {/* Missing/Failed Skills */}
            {allMissingSkills.length > 0 ? (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <AlertCircle className="h-5 w-5 text-amber-500" />
                                    Skills Needing Playbooks
                                </CardTitle>
                                <CardDescription>
                                    {allMissingSkills.length} skills need playbook generation
                                </CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={fetchStatus}
                                    disabled={refreshing}
                                >
                                    <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                                    Refresh
                                </Button>
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={handleRetryAll}
                                    disabled={generating || allMissingSkills.length === 0}
                                    data-testid="retry-all-btn"
                                >
                                    {generating ? (
                                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                        <RotateCcw className="h-4 w-4 mr-1" />
                                    )}
                                    Retry All ({allMissingSkills.length})
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {/* Selection Controls */}
                        <div className="px-4 py-2 border-b flex items-center justify-between bg-muted/30">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    checked={selectedSkills.length === allMissingSkills.length && allMissingSkills.length > 0}
                                    onCheckedChange={() => handleSelectAll(allMissingSkills)}
                                />
                                <span className="text-sm text-muted-foreground">
                                    {selectedSkills.length > 0 ? `${selectedSkills.length} selected` : 'Select all'}
                                </span>
                            </div>
                            {selectedSkills.length > 0 && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleRetrySelected}
                                    disabled={generating}
                                    data-testid="retry-selected-btn"
                                >
                                    {generating ? (
                                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                        <Play className="h-4 w-4 mr-1" />
                                    )}
                                    Retry Selected
                                </Button>
                            )}
                        </div>

                        <ScrollArea className="h-[300px]">
                            <div className="divide-y">
                                {allMissingSkills.map((skill) => (
                                    <div
                                        key={skill.id}
                                        className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors"
                                        data-testid={`skill-row-${skill.id}`}
                                    >
                                        <Checkbox
                                            checked={selectedSkills.includes(skill.id)}
                                            onCheckedChange={() => handleToggleSkill(skill.id)}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{skill.name}</p>
                                            <p className="text-xs text-muted-foreground">{skill.id}</p>
                                        </div>
                                        <Badge 
                                            variant="outline" 
                                            className={skill.status === 'failed' 
                                                ? 'bg-red-500/10 text-red-600 border-red-500/30' 
                                                : 'bg-amber-500/10 text-amber-600 border-amber-500/30'
                                            }
                                        >
                                            {skill.status === 'failed' ? (
                                                <><XCircle className="h-3 w-3 mr-1" />Failed</>
                                            ) : (
                                                <><AlertCircle className="h-3 w-3 mr-1" />Missing</>
                                            )}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardContent className="p-8 text-center">
                        <CheckCircle className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                        <h3 className="text-lg font-medium text-emerald-600">All Playbooks Generated!</h3>
                        <p className="text-sm text-muted-foreground mt-2">
                            All {status?.total_skills} skills have playbooks with content.
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchStatus}
                            className="mt-4"
                            disabled={refreshing}
                        >
                            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                            Refresh Status
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Info Note */}
            <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
                <p className="flex items-center gap-1">
                    <BookOpen className="h-4 w-4" />
                    <strong>Note:</strong> Playbook generation uses AI and may take 5-10 seconds per skill.
                    The page will auto-refresh after 5 seconds when generation is queued.
                </p>
            </div>
        </div>
    );
};

export default PlaybookManager;
